/**
 * CSS styles for changelog preview, pixel-perfect match of the
 * novela-client ChangelogModal + ChangelogSlide rendering.
 *
 * Sources:
 * - novela-client/src/components/ChangelogModal/ChangelogModal.tsx
 * - novela-client/src/components/ChangelogModal/ChangelogSlide.tsx
 * - MUI Joy theme palette & typography
 */

import { PRETENDARD_URL } from "@/components/articles/preview/share-preview-styles";

export { PRETENDARD_URL };

const FONT_FAMILY = [
  "'Pretendard Variable'",
  "Pretendard",
  "-apple-system",
  "BlinkMacSystemFont",
  "system-ui",
  "Roboto",
  "'Helvetica Neue'",
  "'Segoe UI'",
  "'Apple SD Gothic Neo'",
  "'Noto Sans KR'",
  "'Malgun Gothic'",
  "sans-serif",
].join(", ");

export const CHANGELOG_PREVIEW_CSS = `
/* ─── Reset ─── */
*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* ─── Base ─── */
html {
  -webkit-text-size-adjust: 100%;
}

body {
  font-family: ${FONT_FAMILY};
  background-color: rgba(0, 0, 0, 0.4);
  margin: 0;
  padding: 0;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.8);
  letter-spacing: -0.02em;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

img {
  display: block;
  max-width: 100%;
}

/* ─── Modal Backdrop ─── */
.modal-backdrop {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100vh;
  padding: 20px;
}

/* ─── Modal Card ─── */
.modal-card {
  display: flex;
  flex-direction: column;
  width: 520px;
  max-width: 95vw;
  max-height: 80vh;
  background-color: #fafafa;
  box-shadow:
    0px 2px 16px rgba(0, 0, 0, 0.08),
    0px 12px 48px rgba(0, 0, 0, 0.16);
  border-radius: 12px;
  overflow: hidden;
  outline: none;
}

/* ─── Modal Header ─── */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.09);
}

.modal-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-header-title {
  font-size: 16px;
  font-weight: 700;
  line-height: 150%;
  color: rgba(0, 0, 0, 0.87);
}

.modal-header-count {
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: #a1a1a1;
}

.modal-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 6px;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.55);
  transition: background-color 0.15s;
}

.modal-close:hover {
  background-color: rgba(0, 0, 0, 0.06);
}

/* ─── Changelog Slide ─── */
.changelog-slide {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 24px;
  overflow: auto;
  max-height: 60vh;
}

/* ─── Meta Row (date + categories) ─── */
.changelog-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: -4px;
}

.published-date {
  font-size: 14px;
  font-weight: 400;
  line-height: 150%;
  color: #636b74;
}

/* ─── Category Badges ─── */
.category-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: -0.02em;
}

/* ─── Title ─── */
.changelog-title {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.87);
  letter-spacing: -0.02em;
  margin: 0;
}

/* ─── Featured Image ─── */
.featured-image {
  display: block;
  width: calc(100% + 48px);
  max-width: none;
  margin-top: -16px;
  margin-left: -24px;
  margin-right: -24px;
  border-radius: 0;
  height: auto;
}

/* ─── Body Content ─── */
.changelog-body {
  font-size: 16px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.87);
}

.changelog-body p {
  margin: 20px 0;
}

.changelog-body p:empty {
  min-height: 1em;
}

.changelog-body h2 {
  font-size: 1.35em;
  font-weight: 700;
  line-height: 1.3;
  margin-top: 40px;
  margin-bottom: 20px;
}

.changelog-body h3 {
  font-size: 1.15em;
  font-weight: 600;
  line-height: 1.3;
  margin-top: 12px;
  margin-bottom: 4px;
}

.changelog-body ul {
  padding-left: 24px;
  list-style-type: disc;
}

.changelog-body ol {
  padding-left: 24px;
  list-style-type: decimal;
}

.changelog-body ul ul {
  list-style-type: circle;
}

.changelog-body ol ol {
  list-style-type: lower-alpha;
}

.changelog-body li {
  margin-bottom: 4px;
  display: list-item;
  list-style-type: inherit;
}

.changelog-body li > p:first-child {
  margin-top: 0;
}

.changelog-body li > p:last-child {
  margin-bottom: 0;
}

.changelog-body img {
  max-width: 100%;
  border-radius: 6px;
  margin: 8px 0;
}

.changelog-body img[data-width="25%"] { max-width: 25%; }
.changelog-body img[data-width="50%"] { max-width: 50%; }
.changelog-body img[data-width="75%"] { max-width: 75%; }
.changelog-body img[data-width="100%"] { max-width: 100%; }

.changelog-body img[data-align="center"] { margin-left: auto; margin-right: auto; }
.changelog-body img[data-align="left"] { margin-right: auto; }
.changelog-body img[data-align="right"] { margin-left: auto; }

.changelog-body div[data-type="horizontalRule"] {
  margin: 0;
}

.changelog-body hr {
  border: none;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  margin: 42px 0;
}

.changelog-body blockquote {
  border-left: 3px solid #cdd7e1;
  padding-left: 16px;
  margin-left: 0;
  margin-top: 8px;
  margin-bottom: 8px;
  color: #636b74;
  font-style: italic;
}

.changelog-body pre {
  background-color: #f0f4f8;
  border-radius: 6px;
  padding: 16px;
  margin: 8px 0;
  overflow: auto;
}

.changelog-body pre code {
  background-color: transparent;
  padding: 0;
  border-radius: 0;
  font-size: 0.9em;
}

.changelog-body code {
  background-color: #f0f4f8;
  border-radius: 4px;
  padding: 2px 4px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
  font-size: 0.9em;
}

.changelog-body a {
  color: #0b6bcb;
  text-decoration: underline;
  text-decoration-color: #97c3f0;
}

.changelog-body a:hover {
  text-decoration-color: #0b6bcb;
}
`;
